import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, CardContent, Modal, Typography, Box, Stack } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MainCard from 'components/MainCard';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal } from 'store/reducers/actions';
import magnify from 'assets/images/modals/magnify.png';
import axios from 'axios';
import config from 'config';
import { getAuth } from 'firebase/auth';
import fetchPlanInfo from 'services/proAccountServices';

const monthlyPriceId = config.priceIdProUSMonth;
const monthlyPriceIdEU = config.priceIdProEUMonth;

const euCountryCodes = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE'
];

const trialData = {
  trial: true,
  title: 'Try Askpot Pro for FREE for 7 days and get:',
  benefits: [
    'History access',
    'Extended reports: Unique Selling Points, Audience Segmentation, SWOT, Market Overview, Pricing Analysis',
    'Competition analysis',
    'Reviews Analysis'
  ],
  buttonText: 'Start Free Trial',
  priceInfo: {
    trialPeriod: '7 days free',
    price: '79.00/mo',
    unsubscribe: 'Unsubscribe anytime.'
  },
  priceId: monthlyPriceId,
  euPriceId: monthlyPriceIdEU
};

const proData = {
  trial: false,
  title: 'Upgrade to Askpot Pro and get:',
  benefits: [
    'Export to CSV, MS Word',
    'History access',
    'Extended reports: Audience Segmentation, SWOT, Market Overview',
    'Competition analysis',
    'Reviews Analysis',
    '300 credits/month'
  ],
  buttonText: 'Upgrade Now',
  priceInfo: {
    price: '79.00/mo',
    unsubscribe: 'Unsubscribe anytime.'
  },
  priceId: monthlyPriceId,
  euPriceId: monthlyPriceIdEU
};

const FreeBlockModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => state.modalReducer.isOpen);
  const modalData = useSelector((state) => state.modalReducer.modalData);
  // const plan = useSelector(state => state.proAccountReducer.plan);

  useEffect(() => {
    const checkPlanStatus = async () => {
      if (isOpen) {
        try {
          const planInfo = await fetchPlanInfo(dispatch);
          const currentPlan = planInfo.plan;

          // Case 4: If user is already on Pro plan
          if (currentPlan === 'Pro' || currentPlan === 'Pro+' || currentPlan === 'Enterprise Pro Max' || currentPlan === 'Enterprise Pro') {
            window.location.reload();
            return;
          }

          // Case 2: If user is on Trial plan and modal shows trial option
          if (currentPlan === 'Trial' && modalData?.trial === true) {
            window.location.reload();
            return;
          }

          // Case 1 & 3: Free plan or Trial plan with trial:false are handled normally
        } catch (error) {
          console.error('Error checking plan status:', error);
        }
      }
    };

    checkPlanStatus();
  }, []);

  const handleClose = useCallback(() => dispatch(closeModal()), [dispatch]);
  const blockData = useMemo(() => (modalData && modalData.trial ? trialData : proData), [modalData]);
  const [isInEurope, setIsInEurope] = useState(null);
  const [trialIsLoading, setTrialIsLoading] = useState(false);

  const getCountry = useCallback(() => {
    return fetch('https://ipinfo.io/json?token=4f1c89b6de8103')
      .then((response) => response.json())
      .then((jsonResponse) => {
        const countryCode = jsonResponse.country;
        return euCountryCodes.includes(countryCode);
      })
      .catch((error) => {
        console.error('Error fetching country:', error);
        return null; // Return null in case of an error
      });
  }, []);

  useEffect(() => {
    getCountry().then(setIsInEurope);
  }, [getCountry]);

  const currency = isInEurope ? '€' : '$';

  const auth = getAuth();
  const apiUrl = config.apiUrl;

  const handlePlanSelectButton = useCallback(
    (blockData) => async (event) => {
      event.preventDefault();
      setTrialIsLoading(true);

      const user = auth.currentUser;
      if (!user) {
        console.error('No authenticated user found');
        return;
      }
      if (window.gtag) {
        console.log('sending GTM');

        window.gtag('event', 'conversion', {
          send_to: 'AW-16481265161/uSXlCMCs_9YZEInM8LI9',
          value: 39.0,
          currency: 'EUR'
        });
      }
      if (window.ym) {
        console.log('sending YM');
        window.ym(94496474, 'reachGoal', 'START_TRIAL_CLiCK');
      }

      try {
        const token = await user.getIdToken();
        const nonEUpriceId = blockData.priceId;
        const euPriceId = blockData.euPriceId;
        const priceId = isInEurope ? euPriceId : nonEUpriceId;
        const endpointUrl = apiUrl + `/pro_plans/create_checkout_session/${priceId}?trial_days=${blockData.trial}`;

        const response = await axios.get(endpointUrl, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const data = response.data;
        if (data.url && response.status === 200) {
          window.location.href = data.url;
        } else {
          console.error('Draft subscription creation failed:', data.error);
        }
      } catch (error) {
        console.error('Error creating draft subscription or fetching user token:', error);
      }
      setTrialIsLoading(false);
    },
    [auth, apiUrl, isInEurope]
  );

  // Only render the modal if we're not in a reload scenario
  if (!isOpen || !modalData) return null;

  return (
    <Modal open={isOpen} onClose={handleClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
      <MainCard title={modalData.title} modal darkTitle content={false}>
        <CardContent>
          <Typography id="modal-modal-description" variant="body1" gutterBottom>
            {modalData.subtitle}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 2, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" color="primary" gutterBottom>
                {blockData.title}
              </Typography>
              {blockData.benefits.map((benefit, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 1 }} />
                  <Typography variant="body2">{benefit}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ ml: 2 }}>
              <img src={magnify} alt="Illustration" style={{ width: '100px' }} />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start', mb: 2 }}>
            <Button
              disabled={trialIsLoading}
              variant="contained"
              size="large"
              onClick={(event) => {
                handlePlanSelectButton(blockData)(event);
              }}
              sx={{
                background: 'linear-gradient(90deg, #3b82f6 0%, #34d399 100%)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #2563eb 0%, #10b981 100%)'
                },
                boxShadow: 'none'
              }}
            >
              {blockData.buttonText}
            </Button>
            <Stack direction="column" ml={2}>
              {blockData.priceInfo.trialPeriod && (
                <Typography variant="body1" color="textPrimary">
                  <b>{blockData.priceInfo.trialPeriod}</b>, then {currency}
                  {blockData.priceInfo.price}
                </Typography>
              )}
              {!blockData.priceInfo.trialPeriod && (
                <Typography variant="body1" color="textPrimary">
                  {currency}
                  {blockData.priceInfo.price}
                </Typography>
              )}
              <Typography component="div" variant="body1" color="textPrimary" sx={{ mt: 0 }}>
                {blockData.priceInfo.unsubscribe}
                <br />
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                  By continuing checkout, you agree to the{' '}
                  <Typography
                    component="a"
                    variant="body2"
                    target="_blank"
                    href="/maintenance/terms"
                    sx={{
                      color: 'text.secondary'
                    }}
                  >
                    Terms of Use
                  </Typography>{' '}
                  and{' '}
                  <Typography
                    component="a"
                    variant="body2"
                    target="_blank"
                    href="/maintenance/cancellation"
                    sx={{
                      color: 'text.secondary'
                    }}
                  >
                    Cancellation Policy
                  </Typography>
                </Typography>
              </Typography>
            </Stack>
          </Box>
        </CardContent>
      </MainCard>
    </Modal>
  );
};

export default FreeBlockModal;
